/** Staff-only freight figures shared by the internal invoice and bill review. */
export const SHIPPING_MARKUP_POLICY_PCT = 14;
const SHIPPING_MARKUP_MULTIPLIER = 1 + SHIPPING_MARKUP_POLICY_PCT / 100;

function nonnegativeAmount(value: unknown): number | undefined {
	return typeof value === "number" && Number.isFinite(value) && value >= 0
		? value
		: undefined;
}

/** Legacy order metadata may contain a numeric string; null is not a charge. */
export function hasRecordedShippingCharge(value: unknown): boolean {
	return (typeof value === "number" && Number.isFinite(value)) ||
		(typeof value === "string" && Number.isFinite(Number.parseFloat(value)));
}

function record(value: unknown): Record<string, unknown> | undefined {
	return value !== null && typeof value === "object" && !Array.isArray(value)
		? value as Record<string, unknown>
		: undefined;
}

/** Use the same carrier-rate precedence as the staff invoice. */
export function getStoredCarrierQuoteAmount(shippingMetadata: unknown): number | undefined {
	const metadata = record(shippingMetadata);
	const rerate = record(metadata?.carrierRerate);
	const selectedRate = record(metadata?.selectedRate);
	const rerateTotal = nonnegativeAmount(rerate?.selectedRateTotal);
	if (rerateTotal !== undefined) return rerateTotal;
	const carrierQuote = record(metadata?.carrierQuote);
	const carrierTotal = nonnegativeAmount(carrierQuote?.carrierTotal);
	if (carrierTotal !== undefined) return carrierTotal;
	return nonnegativeAmount(
		metadata?.rateTotal ??
		metadata?.rate ??
		selectedRate?.totalPrice ??
		selectedRate?.rate,
	);
}

export function getShippingQuoteBreakdown({
	actualBaseCost,
	chargedToCustomer,
}: {
	actualBaseCost: number | null | undefined;
	chargedToCustomer: number | null | undefined;
}): {
	quoteAmount?: number;
	markupAmount?: number;
	markupPercent?: number;
	quoteEstimated: boolean;
} {
	const actualBase = nonnegativeAmount(actualBaseCost);
	const charged = nonnegativeAmount(chargedToCustomer);
	const quoteAmount = actualBase !== undefined
		? Math.round(actualBase * 100) / 100
		: charged !== undefined && charged > 0
			? Math.round((charged / SHIPPING_MARKUP_MULTIPLIER) * 100) / 100
			: undefined;
	const quoteEstimated = actualBase === undefined && quoteAmount !== undefined;
	const markupAmount = charged !== undefined && charged > 0 &&
		quoteAmount !== undefined && quoteAmount < charged
		? Math.round((charged - quoteAmount) * 100) / 100
		: undefined;
	const markupPercent = markupAmount !== undefined && quoteAmount !== undefined &&
		quoteAmount > 0
		? Math.round((markupAmount / quoteAmount) * 1000) / 10
		: undefined;
	return { quoteAmount, markupAmount, markupPercent, quoteEstimated };
}
